import type { Favorite } from '../store/storeTypes';
import type Nav from '../components/nav/nav';
import type NavigationPanel from './NavigationPanel';
import type Image from '../components/image';
import { SvgManager, getResource } from 'core';
import { wire } from 'hyperhtml';
import type { NavTemplateItem, NavItem } from '../components/nav/navTypes';

type FavoriteNavItem = {
	category: string;
} & NavItem;

const icons = {
	emptyFavorites: '../images/FavoriteOff.svg',
	search: '../images/SavedSearchOverlay.svg',
	more: '../images/More.svg'
};

SvgManager.enqueue(Object.values(icons));

const getEmptyFavoritesTemplate = (context: NavigationPanel) => wire(
	context,
	':favoritesEmptyState'
)`
	<div class="aras-favorite-panel__empty-container">
		<aras-image
			src="${icons.emptyFavorites}"
			class="aras-favorite-panel__empty-icon"
			aria-hidden="true"
		/>
		<div class="aras-favorite-panel__empty-title">
			${getResource('favorite_panel.empty_state_title')}
		</div>
		<div class="aras-favorite-panel__empty-description">
			${getResource('favorite_panel.empty_state_description')}
		</div>
	</div>
`;

const setImageOverlayIcons = (node: Image) => {
	if (node) {
		node.overlayIcons = [icons.search];
	}
};

function navFormatter(item: NavTemplateItem<FavoriteNavItem>): JSX.Element[] {
	return [
		<span className="aras-nav__tree-levelpad" />,
		<aras-image
			ref={item.value.category === 'Search' ? setImageOverlayIcons : null}
			aria-hidden="true"
			className={`aras-nav__row__icon`}
			src={item.value.icon}
		/>,
		<span className="aras-nav__label">{item.value.label}</span>,
		<span className="aras-button aras-button_c aras-nav-leaf-ico">
			<aras-image
				aria-hidden="true"
				className="aras-nav-leaf-ico-image"
				src={icons.more}
			/>
		</span>
	];
}

const getFavoritesNav = (
	context: NavigationPanel,
	favoritesList: Favorite[]
) => {
	const nav: Nav = wire(context, ':favoritesNav')`<aras-nav onclick="${(
		e: Event
	) => context._favoritesClick(e)}" />`;
	nav.formatter = navFormatter;

	const roots = new Set(favoritesList.map(({ id }) => id));
	const items = new Map(
		favoritesList.map((favorite) => [
			favorite.id,
			{
				id: favorite.id,
				label: favorite.label,
				icon: favorite.additional_data.image,
				category: favorite.category
			}
		])
	);

	nav.dataStore.items = items;
	nav.dataStore.roots = roots;
	nav.render();

	return nav;
};

export const getFavoritePanel = (context: NavigationPanel) => {
	const favoritesList = Object.values(context.state.favorites).filter(
		({ category }) => category !== 'ItemType'
	);

	const content = favoritesList.length
		? getFavoritesNav(context, favoritesList)
		: getEmptyFavoritesTemplate(context);

	return wire(context, ':favoritesPanel')`
		<div class="aras-favorite-panel">
			${content}
		</div>
	`;
};
